import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { BehaviorSubject, Observable, Subscription, combineLatest, map, switchMap, take } from 'rxjs';
import { Store } from '@ngrx/store';

import { AppConstants } from '@fcom/booking/app-constants';
import { GaContext, SelectOption } from '@fcom/common/interfaces';
import { ProfileType } from '@fcom/core-api/login';
import {
  ControlData,
  FinnairCart,
  FinnairFrequentFlyerCardItem,
  FinnairOrder,
  FinnairPassengerCode,
  FinnairPassengerItem,
} from '@fcom/dapi/api/models';
import { PaxDetailsFormData } from '@fcom/dapi/interfaces';
import { asPaxTypeKey } from '@fcom/dapi/utils';
import { TypedFormGroup } from '@fcom/service-forms';
import { ButtonTheme, IconPosition, ModalButtons, NotificationTheme } from '@fcom/ui-components';
import { getAirlineNameByCode, rangeFrom, unsubscribe } from '@fcom/core/utils';
import { CommonBookingFeatureState } from '@fcom/common-booking';
import { setSinglePaxDetails } from '@fcom/booking/store/actions/pax-details.actions';
import { PaxDetailsState } from '@fcom/common/interfaces/booking';
import { snapshot } from '@fcom/rx';
import { paxDetails } from '@fcom/booking/store/selectors';
import { BookingQueueService } from '@fcom/booking/services';

import { PassengerDetails, PassengerFormService, PaxFormFields, PrefillSource } from '../../services';

@Component({
  selector: 'fin-passenger-info-list',
  templateUrl: './passenger-info-list.component.html',
  styleUrls: ['./passenger-info-list.component.scss'],
})
export class PassengerInfoListComponent implements OnInit, OnDestroy {
  readonly FinnairPassengerCode = FinnairPassengerCode;
  readonly ButtonTheme = ButtonTheme;
  readonly IconPosition = IconPosition;
  readonly ModalButtons = ModalButtons;
  readonly FIELDS = PaxFormFields;
  readonly NotificationTheme = NotificationTheme;
  public readonly C = AppConstants;

  public readonly TRACKING_CONTEXT = GaContext.BOOKING_FLOW;

  private subscription: Subscription = new Subscription();

  @Input()
  passengers$: Observable<FinnairPassengerItem>[];

  @Input()
  cartData$: Observable<FinnairCart | FinnairOrder>;

  @Input()
  showEdit = false;

  @Input()
  isSaving$: Observable<boolean>;

  @Input()
  allFrequentFlyerCards$: Observable<FinnairFrequentFlyerCardItem[]>;

  @Input()
  controlData$: Observable<ControlData>;

  @Input()
  profileType$: Observable<ProfileType>;

  extendedPassengers$: Observable<
    (FinnairPassengerItem & {
      paxTypeKey: string;
      cards: (FinnairFrequentFlyerCardItem & { airlineName: string })[];
    })[]
  >;

  infantTravelingWithLocked$: Observable<boolean>;
  frequentFlyerAirlines$: Observable<SelectOption[]>;
  passengerGroup$: Observable<TypedFormGroup<UntypedFormGroup | UntypedFormControl>>;

  editingPassenger$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  availableDays: { days$: Observable<number[]>; months: number[]; years: number[] }[];
  formGroupPassanger: PassengerDetails;
  passengerFormGroups: PassengerDetails[];

  constructor(
    private passengerFormService: PassengerFormService,
    private fb: UntypedFormBuilder,
    private store$: Store<CommonBookingFeatureState>,
    private bookingQueueService: BookingQueueService
  ) {}

  ngOnInit(): void {
    this.extendedPassengers$ = combineLatest(
      this.passengers$.map((passenger) =>
        passenger.pipe(
          map((passenger) => ({
            ...passenger,
            paxTypeKey: asPaxTypeKey(passenger.passengerTypeCode).toLowerCase(),
            passengerTypeCode: passenger.passengerTypeCode,
            cards: (passenger.frequentFlyerCards ?? []).map((card) => ({
              ...card,
              airlineName: getAirlineNameByCode(card?.companyCode),
            })),
          }))
        )
      )
    );

    this.subscription.add(
      combineLatest(
        this.passengers$.map((passenger$) =>
          passenger$.pipe(
            take(1),
            switchMap((passenger) => {
              return this.passengerFormService.createPassengerFormGroup(passenger.id).pipe(
                switchMap((passengerFormGroup) => {
                  return this.passengerFormService.prefillData(passengerFormGroup, [PrefillSource.CART]);
                })
              );
            })
          )
        )
      ).subscribe((passengerFormGroups) => {
        this.availableDays = passengerFormGroups.map((passengerFormGroup) => ({
          days$: passengerFormGroup.availableDays$,
          months: rangeFrom(1, 12),
          years: passengerFormGroup.years,
        }));
        passengerFormGroups.forEach((passengerFormGroup, i) =>
          passengerFormGroup.group.addControl('number', this.fb.control(i + 1))
        );

        this.passengerFormGroups = passengerFormGroups;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription = unsubscribe(this.subscription);
  }

  handleSave(paxData: PaxDetailsFormData): void {
    this.store$.dispatch(setSinglePaxDetails({ passenger: paxData }));

    const passengerFormDetails: PaxDetailsState = snapshot(this.store$.pipe(paxDetails()));
    this.bookingQueueService.queuePassengersAndCorporateCodeUpdate(passengerFormDetails);
  }
}
